import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { onAgentShare } from '../servers/commandStream'
import { getAgentShares } from '../transport/relay'

/**
 * Agents shared between friends — owner, 2026-10-17: 使用者可以分享他的 Agent 給他的好朋友來使用.
 *
 * One read serves both sides (what I shared, what was shared with me), and it is re-read when the
 * cloud says a share moved — an offer, an answer, a revoke — never on a clock. A turn starting or
 * ending ("activity") changes no share: the open conversation listens for that itself.
 */
export const AGENT_SHARES_KEY = ['agent-shares'] as const

/** `enabled`: false holds the read back (App asks only while a friend's agent is open). */
export function useAgentShares(enabled = true) {
  const qc = useQueryClient()
  useEffect(
    () =>
      onAgentShare((_id, what) => {
        if (what === 'activity') return
        void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY })
      }),
    [qc],
  )
  return useQuery({ queryKey: AGENT_SHARES_KEY, queryFn: getAgentShares, staleTime: 30_000, enabled })
}

/** How long a share has left, in the fewest words: "23h left", "40m left", or no end at all. */
export function shareLeft(expiresAt: number | null, nowS: number, whose: 'you' | 'they'): string {
  if (expiresAt == null) return whose === 'you' ? 'Until you take it back' : 'Until they take it back'
  const s = Math.max(0, expiresAt - nowS)
  if (s >= 3600) return `${Math.floor(s / 3600)}h left`
  return `${Math.max(1, Math.floor(s / 60))}m left`
}

/** "Bob", "Bob and Carol", "Bob and 2 others" — for the one warning line. */
export function namesLine(names: string[], none: string): string {
  if (names.length === 0) return none
  if (names.length === 1) return names[0]!
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names[0]} and ${names.length - 1} others`
}
